import React, { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { message, Spin } from 'antd';
import { PageTitle } from '@unbooking/ui-modules';
import { useFacility } from '@common/hooks';
import { useRepository } from '@context';
import {
  IDriveCosts,
  IDriveCostsApi,
  IVehicleCosts,
  IVehicleCostsApi,
  IVehicleType,
} from '@common/interfaces';
import { Serializer } from '@common/utils';
import { CostsSettingsAdd } from '@assets/svg/icons';
import { DriveCosts, VehicleCosts } from './components';
import './styles.scss';

const DEFAULT_FUEL_TYPE = 1; // Diesel;

const ServiceCostSettingsPage: FC = () => {
  const {
    agencyName,
    facility: { agencyId, city, costRecoveryByVehicleType, country, facilityId },
  } = useFacility();
  const { financialRepository } = useRepository();
  const { t } = useTranslation();

  const [driveCosts, setDriveCosts] = useState<IDriveCosts[]>([]);
  const [vehicleCosts, setVehicleCosts] = useState<IVehicleCosts[]>([]);

  const [vehicleCostsOptions, setVehicleCostsOptions] = useState<any>();
  const [fuelType, setFuelType] = useState<number>(DEFAULT_FUEL_TYPE);

  const vehicleTypes = vehicleCostsOptions?.actions?.POST?.body?.choices;
  const fuelTypes = vehicleCostsOptions?.actions?.POST?.fuel_type?.choices;
  const fuelOptions =
    fuelTypes?.length > 0
      ? fuelTypes.map(({ value, display_name }: IVehicleType) => ({
          value,
          label: display_name,
        }))
      : null;
  const fuelInitial = fuelOptions?.find(({ value }: { value: number }) => value === fuelType);

  const {
    data: driveCostsData,
    isLoading: isDriveCostLoading,
    refetch: getDriveCosts,
  } = useQuery(
    'get-drive-costs',
    () => financialRepository.getDriveCosts(facilityId, { agencyId }),
    { enabled: !!agencyId, refetchOnWindowFocus: false },
  );

  const { mutate: getVehicleCosts } = useMutation<
    { count: number; results: IVehicleCostsApi[] },
    AxiosError,
    { driveCostId: string; vehicleFuelType: string }
  >(
    'get-vehicle-costs',
    ({ driveCostId, vehicleFuelType }) =>
      financialRepository.getVehicleCosts(facilityId, driveCostId, { fuelType: +vehicleFuelType }),
    {
      onSuccess: (data: { count: number; results: IVehicleCostsApi[] }) => {
        setVehicleCosts(data.results.map(Serializer.formatVehicleCosts));
      },
      onError: (error) => {
        if (error.message) {
          message.error(error.message || t('common.errorMessageDefault'));
        }
      },
    },
  );

  const { mutate: getVehicleCostsOptions, isLoading: isVehicleCostsOptionsLoading } = useMutation<
    unknown,
    AxiosError,
    { driveCostId: string }
  >(
    'get-vehicle-costs-options',
    ({ driveCostId }) => financialRepository.getVehicleCostsOptions(facilityId, driveCostId),
    {
      onSuccess: (data: unknown) => {
        setVehicleCostsOptions(data);
      },
      onError: (error) => {
        if (error.message) {
          message.error(error.message || t('common.errorMessageDefault'));
        }
      },
    },
  );

  const { mutate: updateDriveCosts } = useMutation<
    unknown,
    AxiosError,
    { itemId: string; data: Partial<IDriveCostsApi> }
  >(
    'update-drive-costs',
    ({ itemId, data }) => financialRepository.updateDriveCosts(facilityId, itemId, data),
    {
      onSuccess: () => {
        message.success(t('mobility.messageDriveCostsUpdated'));
        getDriveCosts();
        getVehicleCosts({ driveCostId: driveCosts[0].id, vehicleFuelType: fuelType?.toString() });
      },
      onError: (error) => {
        if (error) {
          message.error(t('common.errorMessageDefault'));
        }
      },
    },
  );

  useEffect(() => {
    if (driveCostsData) {
      setDriveCosts(driveCostsData.results.map(Serializer.formatDriveCosts));
    }
  }, [driveCostsData]);

  useEffect(() => {
    if (driveCosts?.length) {
      getVehicleCostsOptions({ driveCostId: driveCosts[0].id });
      getVehicleCosts({ driveCostId: driveCosts[0].id, vehicleFuelType: fuelType?.toString() });
    }
  }, [driveCosts, fuelType, getVehicleCosts, getVehicleCostsOptions]);

  return (
    <section className="hbh-container service-cost-settings-page">
      <PageTitle
        title={`${t('mobility.serviceCostSettingsTitle')} ${city}, ${country} | ${agencyName}`}
        bottomLine
      />
      <div className="cost-settings">
        <div className="cost-settings-header">
          <h3 className="cost-settings-header-title">
            <CostsSettingsAdd />
            {t('mobility.fleetCostsSettings')}
          </h3>
        </div>
        <Spin size="large" spinning={isDriveCostLoading || isVehicleCostsOptionsLoading}>
          {driveCosts.length > 0 ? (
            <>
              {driveCosts?.map((item: IDriveCosts) => (
                <DriveCosts
                  data={item}
                  key={item.id}
                  updateDriveCosts={(data) => updateDriveCosts({ itemId: item.id, data })}
                />
              ))}
              {costRecoveryByVehicleType ? (
                <VehicleCosts
                  fuelInitial={fuelInitial}
                  fuelOptions={fuelOptions}
                  vehicleCosts={vehicleCosts}
                  vehicleTypes={vehicleTypes}
                  onChangeFuelType={(vehicleFuelType: string) => {
                    setFuelType(Number(vehicleFuelType));
                    getVehicleCosts({ driveCostId: driveCosts[0].id, vehicleFuelType });
                  }}
                />
              ) : null}
            </>
          ) : (
            <div className="cost-settings-empty">{t('mobility.noCostSettings')}</div>
          )}
        </Spin>
      </div>
    </section>
  );
};

export default ServiceCostSettingsPage;
